import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { LOCAL_EN, LOCAL_FR } from "../../const/const";
import { Icon, iconsList } from "../../Icon";
import { StarRating } from "../../StarRating";
import { FormattedMoney } from "../../FormattedMoney";
import { Card } from "../Card";
import styles from "./HotelCard.module.scss";

const HotelCard = ({
	image,
	header,
	link,
	button,
	className,
	style,
	subheader,
	stars,
	minAge,
	days,
	save,
	wasPrice,
	price,
	local,
	labels,
	renderImage,
	renderLink,
}) => (
	<Card className={cx(styles.hotel, className)} style={style}>
		<Card.Header className={cx(styles.hotelHeading)}>
			{header?.length >= 50 ? `${header.substring(0, 50)}...` : header}
		</Card.Header>
		<Card.Image
			className={cx(styles.hotelImage)}
			src={image?.src}
			alt={image?.alt}
			imageComponentProps={{ renderImage }}
		/>
		<Card.Body className={cx(styles.hotelBody)}>
			{subheader && (
				<Card.Subheader className={cx(styles.hotelSubheading)}>{subheader}</Card.Subheader>
			)}
			<div className={styles.hotelRatingsContainer}>
				{stars && (
					<div className={styles.hotelDetailsStars}>
						<StarRating rating={stars} />
					</div>
				)}
				{minAge >= 16 && (
					<p className={styles.hotelDetailsAdultsOnly}>{`Adults only ${minAge}+`}</p>
				)}
			</div>
			{days && (
				<div className={cx(styles.hotelDetailsDays)}>
					<span>{labels?.departureDate}</span>
					<span>{days}</span>
					<span>{labels?.allInc && labels?.allInc}</span>
				</div>
			)}
			{price && (
				<div className={cx(styles.hotelDetailsPrice)}>
					<div className={cx(styles.hotelDetailsSave)}>{save}</div>
					{wasPrice && (
						<div className={cx(styles.hotelDetailsWas)}>
							{`${labels?.was} `}
							<span>
								<FormattedMoney number={wasPrice} local={local} />
							</span>
						</div>
					)}
					<div className={cx(styles.hotelDetailsAmount)}>
						<FormattedMoney number={price} local={local} />
						{labels?.perAdult && <span>{labels?.perAdult}</span>}
					</div>
					{labels?.taxAndFee && labels?.taxAndFee}
				</div>
			)}
			{button && (
				<div className={styles.hotelButtonsContainer}>
					{button?.to && (
						<Card.Button to={button?.to} size="sm" linkComponentProps={{ renderLink }}>
							{button?.icon && <Icon name={button?.icon} />}
							{button?.text}
						</Card.Button>
					)}
					{link?.to && (
						<Card.Link
							to={link.to}
							target={link?.target}
							className={cx(styles.hotelButtonsLink)}
							renderLink={renderLink}>
							<span className={cx(styles.hotelButtonsLinkText)}>
								{link.text}
								{link?.icon && <Icon name={link?.icon} />}
							</span>
						</Card.Link>
					)}
				</div>
			)}
		</Card.Body>
		{link?.to && (
			<Card.AsLink
				link={link.to}
				target={link?.target}
				text={subheader && subheader}
				className={cx(styles.hotelCardLink)}
				renderLink={renderLink}
			/>
		)}
	</Card>
);

HotelCard.propTypes = {
	image: PropTypes.shape({
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		alt: PropTypes.string,
		src: PropTypes.string,
	}).isRequired,
	header: PropTypes.string.isRequired,
	subheader: PropTypes.string,
	link: PropTypes.shape({
		to: PropTypes.string.isRequired,
		target: PropTypes.string,
		icon: PropTypes.oneOf(iconsList),
		text: PropTypes.string,
	}).isRequired,
	icon: PropTypes.oneOf(iconsList),
	button: PropTypes.shape({
		icon: PropTypes.oneOf(iconsList),
		iconPlacement: PropTypes.oneOf(["right", "left"]),
		onClick: PropTypes.func,
		text: PropTypes.string,
		to: PropTypes.string,
	}),
	stars: PropTypes.number,
	minAge: PropTypes.number,
	days: PropTypes.string,
	save: PropTypes.string,
	wasPrice: PropTypes.number,
	price: PropTypes.number,
	local: PropTypes.oneOf([LOCAL_FR, LOCAL_EN]),
	labels: PropTypes.shape({
		allInc: PropTypes.string,
		was: PropTypes.string,
		perAdult: PropTypes.string,
		taxAndFee: PropTypes.string,
		departureDate: PropTypes.string,
	}),
	className: PropTypes.string,
	style: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	renderImage: PropTypes.func,
	renderLink: PropTypes.func,
};

HotelCard.defaultProps = {
	className: undefined,
	style: {},
	subheader: null,
	icon: "arrow-right",
	button: undefined,
	stars: undefined,
	minAge: undefined,
	days: undefined,
	save: undefined,
	wasPrice: undefined,
	price: undefined,
	local: LOCAL_EN,
	labels: {},
	renderImage: undefined,
	renderLink: undefined,
};

export default HotelCard;
export { HotelCard };
